import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchApiData } from "../../../redux/reducers/commonApiSlice";
import { convertStraight, formatDateTime } from "../../common-data/Converts";
import {
  AlertIcon,
  CalendarIcon,
  ClockIcon,
  PersonIcon1,
} from "../../../images/icons/icons";
import CalendarLoader from "../../common-components/CalendarLoader";

const MemberBCALogs = ({ activeTab, userInfoData, rowData }) => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((state) => state.common);

  const [allMemberFitnessInfo, setAllMemberFitnessInfo] = useState([]);
  const [pagination, setPagination] = useState([]);

  const getMemberInfo = useCallback(
    (filters) => {
      const endpoint = "/member/fitness_profiles/bca_logs";

      dispatch(
        fetchApiData({
          method: "GET",
          endpoint: endpoint,
          payload: {
            filters: filters,
          },
          token: userInfoData?.token,
        })
      );
    },
    [dispatch, userInfoData?.token]
  );

  const getStatusBadge = (status) => {
    const statusMap = {
      upcoming: {
        label: "Upcoming",
        className: "member-profile-bca-badge-upcoming",
      },
      completed: {
        label: "Completed",
        className: "member-profile-bca-badge-completed",
      },
      pending: {
        label: "Pending",
        className: "member-profile-bca-badge-pending",
      },
      missed: { label: "Missed", className: "member-profile-bca-badge-missed" },
    };
    return (
      statusMap[status] || {
        label: status,
        className: "member-profile-bca-badge-default",
      }
    );
  };

  const getIconBackgroundColor = (status) => {
    const colorMap = {
      upcoming: "#dbeafe",
      completed: "#d1fae5",
      missed: "#fee2e2",
      pending: "#fef3c7",
    };
    return colorMap[status] || "#fee2e2";
  };

  const getIconColor = (status) => {
    const colorMap = {
      upcoming: "#3b82f6",
      completed: "#10b981",
      missed: "#dc2626",
      pending: "#f59e0b",
    };
    return colorMap[status] || "#ef4443";
  };

  useEffect(() => {
    if (activeTab === "bca-profile") {
      if (rowData?.user_id) {
        const sendableFilters = {
          user_id: rowData?.user_id || "",
          page: 1,
          limit: 1000,
        };

        getMemberInfo(sendableFilters);
      } else if (!rowData?.user_id) {
        setAllMemberFitnessInfo([]);
        setPagination([]);
      }
    }
  }, [activeTab, rowData?.user_id, getMemberInfo]);

  useEffect(() => {
    if (activeTab === "bca-profile" && rowData?.user_id) {
      if (!loading && !error && data?.status && data?.data) {
        setAllMemberFitnessInfo(data.data);
        setPagination(data?.pagination);
      } else {
        setAllMemberFitnessInfo([]);
        setPagination([]);
      }
    }
  }, [activeTab, data, loading, error, rowData?.user_id]);

  if (!loading && (error || allMemberFitnessInfo?.length === 0)) {
    return (
      <div className="error-container">
        <p>No BCA logs found for this member.</p>
      </div>
    );
  }

  return (
    <div className="member-profile-bca-logs-container">
      <h3 className="member-profile-logs-total">
        Total: {pagination?.[0]?.pagination || allMemberFitnessInfo.length} logs
      </h3>

      <div className="member-profile-logs-wrapper">
        {loading ? (
          <CalendarLoader daysCount={3} />
        ) : (
          allMemberFitnessInfo.map((log, index) => {
            const statusBadge = getStatusBadge(log.status);

            return (
              <div key={index} className="member-profile-bca-log-card">
                <div className="member-profile-bca-header">
                  <h2 className="member-profile-bca-title">
                    BCA Log <b>{allMemberFitnessInfo.length - index}</b>
                  </h2>
                  <span className={statusBadge.className}>
                    {statusBadge.label}
                  </span>
                </div>

                <p className="member-profile-bca-date-added">
                  Added on:{" "}
                  {log.date_added ? convertStraight(log.date_added) : "-"}
                </p>

                <div className="member-profile-bca-scheduled-section">
                  <div className="member-profile-bca-scheduled-header">
                    <div>
                      <p className="member-profile-bca-scheduled-label">
                        Scheduled Date & Time
                      </p>
                      <h3 className="member-profile-bca-scheduled-datetime">
                        {formatDateTime(log.scheduled_date)}
                      </h3>
                    </div>
                    {log.status === "completed" && log.bca_report && (
                      <a
                        href={log.bca_report}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="member-profile-bca-download-btn"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          style={{ marginRight: "8px" }}
                        >
                          <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                        </svg>
                        Download Report
                      </a>
                    )}
                  </div>
                </div>

                <div className="member-profile-bca-info-grid">
                  <div className="member-profile-bca-info-card">
                    <div
                      className="member-profile-bca-info-icon"
                      style={{ background: getIconBackgroundColor(log.status) }}
                    >
                      <PersonIcon1 color={getIconColor(log.status)} />
                    </div>
                    <div className="member-profile-bca-info-content">
                      <p className="member-profile-bca-info-label">Trainer</p>
                      <p className="member-profile-bca-info-value">
                        {log.scheduled_trainer_name || log.trainer_name || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="member-profile-bca-info-card">
                    <div
                      className="member-profile-bca-info-icon"
                      style={{ background: getIconBackgroundColor(log.status) }}
                    >
                      <CalendarIcon color={getIconColor(log.status)} />
                    </div>
                    <div className="member-profile-bca-info-content">
                      <p className="member-profile-bca-info-label">
                        Scheduled Date
                      </p>
                      <p className="member-profile-bca-info-value">
                        {log.scheduled_date
                          ? convertStraight(log.scheduled_date)
                          : "-"}
                      </p>
                    </div>
                  </div>

                  <div className="member-profile-bca-info-card">
                    <div
                      className="member-profile-bca-info-icon"
                      style={{
                        background: getIconBackgroundColor(log.status),
                      }}
                    >
                      <ClockIcon color={getIconColor(log.status)} />
                    </div>
                    <div className="member-profile-bca-info-content">
                      <p className="member-profile-bca-info-label">
                        Completed On
                      </p>
                      <p className="member-profile-bca-info-value">
                        {log.completed_on
                          ? convertStraight(log.completed_on)
                          : "-"}
                      </p>
                    </div>
                  </div>

                  <div className="member-profile-bca-info-card">
                    <div
                      className="member-profile-bca-info-icon"
                      style={{ background: getIconBackgroundColor(log.status) }}
                    >
                      <AlertIcon color={getIconColor(log.status)} />
                    </div>
                    <div className="member-profile-bca-info-content">
                      <p className="member-profile-bca-info-label">
                        BCA Status
                      </p>
                      <span className={statusBadge.className}>
                        {statusBadge.label}
                      </span>
                    </div>
                  </div>
                </div>

                {log.next_scheduled_date && (
                  <div className="member-profile-bca-next-section">
                    <div className="member-profile-bca-next-header">
                      <h4 className="member-profile-bca-next-title">
                        Next Scheduled BCA
                      </h4>
                    </div>
                    <div className="member-profile-bca-next-content">
                      <div className="member-profile-bca-next-info-item">
                        <span className="member-profile-bca-next-label">
                          Date
                        </span>
                        <span className="member-profile-bca-next-value">
                          {convertStraight(log.next_scheduled_date)}
                        </span>
                      </div>
                      <div className="member-profile-bca-next-info-item">
                        <span className="member-profile-bca-next-label">
                          Trainer
                        </span>
                        <span className="member-profile-bca-next-value">
                          {log.scheduled_trainer_name ||
                            log.trainer_name ||
                            "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MemberBCALogs;
